import { useEffect, useState } from "react";
import { Header } from "@/components/Header";
import { ProfileSidebarAnfitriao } from "@/components/ProfileSidebarAnfitriao";
import {
  HeroBannerAnfitriao,
  SummarySection,
  ServicosSection,
  ReservasSectionAnfitriao,
  AvaliacoesSectionAnfitriao,
} from "@/components/AnfitriaoDashboardSections";
import ServicoDetalhesModal from "@/components/ServicoDetalhesModal";
import type { AnfitriaoDashboardData } from "@/types/anfitriao-dashboard";
import AnfitriaoPerfilEditar from "@/pages/AnfitriaoPerfilEditar";
import ReservaDetalhesModal from "@/components/ReservaDetalhesModal";
import AnfitriaoReservas from "@/pages/AnfitriaoReservas";

export default function AnfitriaoDashboard() {
  const [data, setData] = useState<AnfitriaoDashboardData | null>(null);
  const [loading, setLoading] = useState(true);
  const [modalOpen, setModalOpen] = useState(false);
  const [servicoSelecionado, setServicoSelecionado] = useState<any>(null);
  const [abaAtiva, setAbaAtiva] = useState<"dashboard" | "perfil" | "reservas" | "financeiro" | "avaliacoes" | "ia">("dashboard");

  type Pet = {
    id?: number;
    nome?: string;
    especie?: string;
    raca?: string;
    porte?: string;
    idade?: string;
  };

  type ReservaDetalhe = {
    id: number;
    tutor: string;
    telefone_tutor?: string;
    servico: string;
    status: string;
    data_inicio?: string;
    data_fim?: string;
    hora_inicio?: string;
    hora_fim?: string;
    mensagem?: string;
    valor_total?: number;
    valor_liquido?: number;
    pets?: Pet[];
  };

  const [modalReservaOpen, setModalReservaOpen] = useState(false);
  const [reservaSelecionada, setReservaSelecionada] = useState<ReservaDetalhe | null>(null);

  function abrirDetalhesReserva(reserva: ReservaDetalhe) {
    setReservaSelecionada(reserva);
    setModalReservaOpen(true);
  }

  function fecharDetalhesReserva() {
    setModalReservaOpen(false);
    setReservaSelecionada(null);
  }

  const abrirModal = (servico: any) => {
    setServicoSelecionado(servico);
    setModalOpen(true);
  };

  const fecharModal = () => {
    setModalOpen(false);
    setServicoSelecionado(null);
  };

  const irParaEditarServico = (servicoId: number) => {
    fecharModal();
    window.location.href = `/anfitriao/dashboard?aba=perfil&secao=servicos&servico_id=${servicoId}`;
  };

  const irParaAgenda = (servicoId?: number) => {
    fecharModal();

    if (!data?.links?.agenda) return;

    const url = new URL(data.links.agenda, window.location.origin);

    if (servicoId) {
      url.searchParams.set("servico_id", String(servicoId));
    }

    window.location.href = url.toString();
  };

  useEffect(() => {
    const syncAbaComUrl = () => {
      const url = new URL(window.location.href);
      const aba = url.searchParams.get("aba");

      if (aba === "perfil") {
        setAbaAtiva("perfil");
      } else if (aba === "reservas") {
        setAbaAtiva("reservas");
      } else if (aba === "financeiro") {
        setAbaAtiva("financeiro");
      } else if (aba === "avaliacoes") {
        setAbaAtiva("avaliacoes");
      } else if (aba === "ia") {
        setAbaAtiva("ia");
      } else {
        setAbaAtiva("dashboard");
      }
    };

    syncAbaComUrl();
    window.addEventListener("popstate", syncAbaComUrl);

    return () => {
      window.removeEventListener("popstate", syncAbaComUrl);
    };
  }, []);

  useEffect(() => {
    if (abaAtiva !== "dashboard") {
      fecharModal();
    }
  }, [abaAtiva]);

  useEffect(() => {
    fetch("/anfitriao/dashboard/data", {
      credentials: "same-origin",
      headers: {
        Accept: "application/json",
      },
    })
      .then(async (res) => {
        if (!res.ok) throw new Error("Erro ao carregar dashboard");
        return res.json();
      })
      .then(setData)
      .finally(() => setLoading(false));
  }, []);

  if (loading) {
    return <div className="p-6">Carregando dashboard...</div>;
  }

  if (!data) {
    return <div className="p-6">Não foi possível carregar os dados.</div>;
  }

  const firstName = data.user.name?.split(" ")[0] || "Anfitrião";
  const cidadeEstado =
    [data.user.cidade, data.user.estado].filter(Boolean).join(" - ") || "Sua região";
  const perfilPct = Math.max(0, Math.min(100, data.profile.perfil_pct || 0));

  return (
    <>
      <Header activeTab="anfitrioes" isCompact hideAudienceNav />

      <div className="min-h-screen gradient-hero">
        <div className="w-full max-w-7xl mx-auto px-3 sm:px-4 md:px-6 py-4 md:py-8">
          <div className="flex flex-col lg:flex-row gap-4 md:gap-6">
            <aside className="w-full lg:w-72 shrink-0">
              <ProfileSidebarAnfitriao
                userName={firstName}
                bio={data.profile.bio || ""}
                avatarUrl={data.profile.avatar_url || ""}
                perfilPct={perfilPct}
                cidadeEstado={cidadeEstado}
                links={data.links}
                isMv={data.summary.is_mv}
                profileItems={data.profile.profileItems || data.profile.profile_items || []}
                abaAtiva={abaAtiva}
                onNavigate={(aba) => {
                  const destino =
                    aba === "dashboard"
                      ? "/anfitriao/dashboard"
                      : `/anfitriao/dashboard?aba=${aba}`;

                  window.location.href = destino;
                }}
              />
            </aside>

            <main className="flex-1 min-w-0">
              {abaAtiva === "dashboard" && (
                <div className="space-y-5">
                  <HeroBannerAnfitriao firstName={firstName} links={data.links} />
                  <SummarySection summary={data.summary} />

                  <ServicosSection
                    servicos={data.servicos}
                    onServicoClick={abrirModal}
                  />

                  <ReservasSectionAnfitriao
                    reservas={data.proximas_reservas}
                    links={data.links}
                  />
                </div>
              )}

              {abaAtiva === "perfil" && (
                <div className="w-full space-y-5">
                  <AnfitriaoPerfilEditar embedded />
                </div>
              )}

              {abaAtiva === "reservas" && (
                <div className="w-full">
                  <AnfitriaoReservas />
                </div>
              )}

              {abaAtiva === "financeiro" && (
                <div className="w-full">
                  <div className="rounded-2xl border bg-white overflow-hidden">
                    <iframe
                      src="/anfitriao/financeiro"
                      title="Financeiro do anfitrião"
                      className="w-full min-h-[80vh]"
                    />
                  </div>
                </div>
              )}

              {abaAtiva === "avaliacoes" && (
                <div className="w-full">
                  <AvaliacoesSectionAnfitriao avaliacoes={data.avaliacoes} />
                </div>
              )}

              {abaAtiva === "ia" && (
                <div className="w-full">
                  <div className="rounded-2xl border bg-white overflow-hidden">
                    <iframe
                      src="/painel/chat-suporte"
                      title="Tire suas dúvidas"
                      className="w-full min-h-[80vh]"
                    />
                  </div>
                </div>
              )}
              
            </main>
          </div>
        </div>
      </div>

      <ServicoDetalhesModal
        open={modalOpen}
        servico={servicoSelecionado}
        onClose={fecharModal}
        onEditar={(id) => irParaEditarServico(id)}
        onAgenda={(id) => irParaAgenda(id)}
      />
    </>
  );
}
